import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import imgActionsDropdowns from '../../../assets/images/docs/editor-actions-dropdowns.png';
import { Body, CodeBlock, MenuPath, Note, SectionDivider, Tip, VarCard } from '../ui';

// ── Page ────────────────────────────────────────────────────────────────────

const ActionsDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary">
      {/* -- What are Actions? -- */}
      <h2
        id="what-are-actions"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.actions.title')}
      </h2>
      <img
        src={imgActionsDropdowns}
        alt="Actions dropdowns in the TouchPose editor"
        className="block max-w-full w-auto rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)] mb-[20px]"
      />
      <Body>
        <Trans
          i18nKey="docs.actions.intro1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.actions.intro2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <SectionDivider />

      {/* -- Event Types -- */}
      <h2
        id="event-types"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.actions.eventTypesTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.actions.eventTypesBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <table className="w-full border-collapse text-[13px] mb-[20px]">
        <thead>
          <tr className="border-b-2 border-surface-hover">
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.eventTableEvent')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.eventTableInteger')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.eventTableTypicalUse')}
            </th>
          </tr>
        </thead>
        <tbody>
          {[
            [
              t('docs.actions.eventRightClick'),
              '0',
              t('docs.actions.eventRightClickUse')
            ],
            [
              t('docs.actions.eventDoubleClick'),
              '1',
              t('docs.actions.eventDoubleClickUse')
            ],
            [
              t('docs.actions.eventCtrlRightClick'),
              '2',
              t('docs.actions.eventCtrlRightClickUse')
            ],
            [
              t('docs.actions.eventCtrlDoubleClick'),
              '3',
              t('docs.actions.eventCtrlDoubleClickUse')
            ],
            [
              t('docs.actions.eventShiftRightClick'),
              '4',
              t('docs.actions.eventShiftRightClickUse')
            ],
            [
              t('docs.actions.eventShiftDoubleClick'),
              '5',
              t('docs.actions.eventShiftDoubleClickUse')
            ],
            [
              t('docs.actions.eventCtrlShiftRightClick'),
              '6',
              t('docs.actions.eventCtrlShiftRightClickUse')
            ],
            [
              t('docs.actions.eventCtrlShiftDoubleClick'),
              '7',
              t('docs.actions.eventCtrlShiftDoubleClickUse')
            ],
            [t('docs.actions.eventAny'), '8', t('docs.actions.eventAnyUse')]
          ].map(([label, val, use], i) => (
            <tr
              key={i}
              className={`border-b border-surface-secondary ${i % 2 === 0 ? 'bg-surface-base' : 'bg-surface'}`}
            >
              <td className="px-[12px] py-[8px] text-content-secondary">{label}</td>
              <td className="px-[12px] py-[8px]">
                <MenuPath>{val}</MenuPath>
              </td>
              <td className="px-[12px] py-[8px] text-slate-500 text-[12px]">
                {use}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionDivider />

      {/* -- Managing Actions -- */}
      <h2
        id="managing-actions"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.actions.managingActionsTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.actions.managingActionsBody1"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.actions.managingActionsBody2"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.actions.creatingActionTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.actions.creatingActionBody"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.actions.editingScriptTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.actions.editingScriptBody"
          components={{ strong: <strong /> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.actions.renamingActionTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.actions.renamingActionBody"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.actions.deletingActionTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.actions.deletingActionBody"
          components={{ strong: <strong /> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.actions.assigningActionTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.actions.assigningActionBody"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <Note label={t('docs.actions.noteLabel')}>
        <Trans
          i18nKey="docs.actions.assigningActionNote"
          components={{ strong: <strong /> }}
        />
      </Note>

      <SectionDivider />

      {/* -- Coding Actions -- */}
      <h2
        id="coding-actions"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.actions.codingActionsTitle')}
      </h2>
      <Body>{t('docs.actions.codingActionsBody')}</Body>

      <h3 id="injected-variables" className="font-display text-[18px] font-bold text-content-primary mt-[24px] mb-[6px] tracking-[-0.2px]">
        {t('docs.actions.injectedVariablesTitle')}
      </h3>

      <VarCard name="touch_control" type="str" example='"arm_R_ctrl"'>
        <Trans
          i18nKey="docs.actions.touchControlDesc"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </VarCard>

      <VarCard name="touch_event" type="int" example="0">
        {t('docs.actions.touchEventDesc')}
      </VarCard>

      <VarCard name="custom_return" type="any" example="touch_menu">
        <Trans
          i18nKey="docs.actions.customReturnDesc"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </VarCard>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[24px] mb-[6px]">
        {t('docs.actions.branchingOnEventTitle')}
      </h3>
      <CodeBlock>
        {`import maya.cmds as mc

# touch_event matches the event integer from the table
if touch_event == 0:
    # right-click
    mc.select(touch_control)
elif touch_event == 1:
    # double-click
    mc.select(touch_control, add=True)`}
      </CodeBlock>

      <SectionDivider />

      {/* -- Selection Buffer -- */}
      <h2
        id="selection-buffer"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.actions.selectionBufferTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.actions.selectionBufferBody1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.actions.selectionBufferBody2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.actions.selectionBufferApiTitle')}
      </h3>

      <table className="w-full border-collapse text-[13px] mb-[20px]">
        <thead>
          <tr className="border-b-2 border-surface-hover">
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.selectionBufferTableCall')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.selectionBufferTableReturns')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.selectionBufferTableDescription')}
            </th>
          </tr>
        </thead>
        <tbody>
          {[
            [
              'selection_buffer.initialize()',
              '—',
              t('docs.actions.selectionBufferInitDesc')
            ],
            [
              'selection_buffer.get_previous_selection()',
              'list[str]',
              t('docs.actions.selectionBufferGetPrevDesc')
            ],
            [
              'selection_buffer.cleanup()',
              '—',
              t('docs.actions.selectionBufferCleanupDesc')
            ]
          ].map(([call, ret, desc], i) => (
            <tr
              key={i}
              className={`border-b border-surface-secondary ${i % 2 === 0 ? 'bg-surface-base' : 'bg-surface'}`}
            >
              <td className="px-[12px] py-[8px]">
                <MenuPath>{call}</MenuPath>
              </td>
              <td className="px-[12px] py-[8px] text-slate-500">{ret}</td>
              <td className="px-[12px] py-[8px] text-content-secondary text-[12px]">
                {desc}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.actions.selectionBufferExampleTitle')}
      </h3>
      <CodeBlock>
        {`from touchpose import selection_buffer

# initialize() is safe to call at the top of every script
selection_buffer.initialize()

# get_previous_selection() returns what was selected before the double-click fired
prev_selection = selection_buffer.get_previous_selection()

# touch_control is always the clicked control, regardless of current Maya selection
controls_to_mirror = [touch_control] + prev_selection`}
      </CodeBlock>

      <Note label={t('docs.actions.noteLabel')}>
        <Trans
          i18nKey="docs.actions.selectionBufferNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <SectionDivider />

      {/* -- Modifier Keys -- */}
      <h2
        id="modifier-keys"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.actions.modifierKeysTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.actions.modifierKeysBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <table className="w-full border-collapse text-[13px] mb-[20px]">
        <thead>
          <tr className="border-b-2 border-surface-hover">
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.modifierTableKey')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.modifierTableBitValue')}
            </th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Shift', '1'],
            ['Caps Lock', '2'],
            ['Ctrl', '4'],
            ['Alt', '8']
          ].map(([key, val], i) => (
            <tr
              key={i}
              className={`border-b border-surface-secondary ${i % 2 === 0 ? 'bg-surface-base' : 'bg-surface'}`}
            >
              <td className="px-[12px] py-[8px] text-content-secondary">{key}</td>
              <td className="px-[12px] py-[8px]">
                <MenuPath>{val}</MenuPath>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <CodeBlock>
        {`import maya.cmds as mc

mods = mc.getModifiers()
ctrl_held  = bool(mods & 4)
shift_held = bool(mods & 1)

if ctrl_held:
    mc.select(touch_control, add=True)
else:
    mc.select(touch_control)`}
      </CodeBlock>

      <Tip label={t('docs.actions.tipLabel')}>
        <Trans
          i18nKey="docs.actions.modifierTip"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Tip>

      <SectionDivider />

      {/* -- Hotkeys -- */}
      <h2
        id="hotkeys"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.actions.hotkeysTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.actions.hotkeysBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 id="hotkey-api" className="font-display text-[18px] font-bold text-content-primary mt-[24px] mb-[10px] tracking-[-0.2px]">
        {t('docs.actions.hotkeyApiTitle')}
      </h3>

      <table className="w-full border-collapse text-[13px] mb-[20px]">
        <thead>
          <tr className="border-b-2 border-surface-hover">
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.hotkeyTableCall')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.hotkeyTableReturns')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.actions.hotkeyTableDescription')}
            </th>
          </tr>
        </thead>
        <tbody>
          {[
            ['hotkey.bind_key("g")', '—', t('docs.actions.hotkeyBindDesc')],
            ['hotkey.unbind_key("g")', '—', t('docs.actions.hotkeyUnbindDesc')],
            ['hotkey.clear_bound_keys()', '—', t('docs.actions.hotkeyClearDesc')],
            [
              'hotkey.get_pressed_key("g")',
              'bool',
              t('docs.actions.hotkeyGetPressedDesc')
            ]
          ].map(([call, ret, desc], i) => (
            <tr
              key={i}
              className={`border-b border-surface-secondary ${i % 2 === 0 ? 'bg-surface-base' : 'bg-surface'}`}
            >
              <td className="px-[12px] py-[8px]">
                <MenuPath>{call}</MenuPath>
              </td>
              <td className="px-[12px] py-[8px] text-slate-500">{ret}</td>
              <td className="px-[12px] py-[8px] text-content-secondary text-[12px]">
                {desc}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Note label={t('docs.actions.noteLabel')}>
        <Trans
          i18nKey="docs.actions.hotkeyReservedNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[24px] mb-[6px]">
        {t('docs.actions.hotkeyExampleTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.actions.hotkeyExampleBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <CodeBlock>
        {`from touchpose import hotkey

# bind_key is idempotent — safe to call on every action fire
hotkey.bind_key("g")

if hotkey.get_pressed_key("g"):
    # G was held — select all controls in the group
    mc.select(group_controls, replace=True)
else:
    # Normal click — select the clicked control only
    mc.select(touch_control)`}
      </CodeBlock>

      <Tip label={t('docs.actions.tipLabel')}>
        <Trans
          i18nKey="docs.actions.hotkeyTip"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Tip>
    </article>
  );
};

export default ActionsDoc;
